// API Base URL
const API_BASE_URL = 'https://tsunami-data.robert-9e7.workers.dev/api';

// Import offline storage utilities
import { storeOfflineReport, initDatabase } from './offlineStorage';
import { registerBackgroundSync } from './syncService';

/**
 * Get the authentication token from Rownd
 * @param {boolean} waitForToken - Whether to wait for a token if not immediately available
 * @returns {Promise<string>} The authentication token
 */
export const getAuthToken = async (waitForToken = true) => {
  try {
    // Get window.rownd from global instance if it exists
    if (typeof window !== 'undefined' && window.rownd) {
      return await window.rownd.getAccessToken({ waitForToken });
    }
    
    // No Rownd instance available
    console.warn('Rownd not initialized. Returning null token.');
    return null;
  } catch (error) {
    console.error('Error getting Rownd token:', error);
    return null;
  }
};

/**
 * Check if we are online
 * @returns {boolean} Whether we're online
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Make an authenticated API request
 * @param {string} endpoint - The API endpoint to call
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} The API response
 */
export const fetchAuthenticated = async (endpoint, options = {}) => {
  try {
    // Check if we're online before even attempting the fetch
    if (!navigator.onLine && options.method !== 'GET') {
      console.log(`Device is offline, handling offline mode for ${endpoint}`);
      
      // For POST requests to /reports, we can store them locally
      if (options.method === 'POST' && endpoint === '/reports') {
        console.log('Handling offline report submission');
        return handleOfflineSubmission(options.body);
      }
      
      throw new Error('OFFLINE_MODE');
    }

    const token = await getAuthToken();

    // Set up the request headers with authentication
    const headers = {
      ...(options.headers || {}),
      'Authorization': token ? `Bearer ${token}` : ''
    };

    // If we're sending JSON data, add the content-type header
    if (options.body && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Make the authenticated request
    console.log(`Making API request to ${endpoint}`);
    let response;
    try {
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers
      });
    } catch (networkError) {
      console.error('Network error during fetch:', networkError);
      // If we have a network error and this is a POST to /reports, handle it as offline
      if (options.method === 'POST' && endpoint === '/reports') {
        console.log('Network error - handling as offline submission');
        return handleOfflineSubmission(options.body);
      }
      throw networkError;
    }

    // Parse the response
    const data = await response.json();

    // Check for API errors
    if (!data.success) {
      throw new Error(data.error || 'API error');
    }

    return data;
  } catch (error) {
    // If we're offline or have a network error, we can handle it here
    if (error.message === 'OFFLINE_MODE' || 
        error.name === 'TypeError' ||
        error.message.includes('Failed to fetch')) {
      console.warn('Device is offline or network error, handling offline mode');
      
      // For POST requests to /reports, we can store them locally
      if (options.method === 'POST' && endpoint === '/reports') {
        console.log('Handling offline report submission from catch block');
        return handleOfflineSubmission(options.body);
      }
      
      throw new Error('You are offline. Your data will be saved and submitted when you reconnect.');
    }
    
    console.error('API request failed:', error);
    throw error;
  }
};

/**
 * Handle offline report submission
 * @param {FormData} formData - The form data to submit
 * @returns {Promise<Object>} The response object
 */
const handleOfflineSubmission = async (formData) => {
  try {
    // Extract the report data and image from FormData
    let reportData = null;
    let imageFile = null;
    
    for (const [key, value] of formData.entries()) {
      if (key === 'data') {
        try {
          reportData = JSON.parse(value);
        } catch (e) {
          console.error('Error parsing report data:', e);
          throw new Error('Invalid report data format');
        }
      } else if (key === 'images') {
        imageFile = value;
      }
    }
    
    if (!reportData) {
      throw new Error('No report data found in form data');
    }
    
    console.log('Storing offline report:', reportData);
    if (imageFile) {
      console.log('With image:', imageFile.name, imageFile.type, imageFile.size);
    } else {
      console.log('No image included with report');
    }
    
    // Validate the image size if present
    if (imageFile && imageFile.size > 10 * 1024 * 1024) { // 10MB limit
      console.warn('Image too large for offline storage:', imageFile.size);
      // Store without the image
      const reportId = await storeOfflineReport(reportData, null);
      console.log('Offline report stored with ID (without image):', reportId);
      
      // Try to register background sync
      const syncRegistered = await registerBackgroundSync();
      console.log('Background sync registered:', syncRegistered);
      
      return {
        success: true,
        offline: true,
        warning: 'Image was too large for offline storage and will not be included.',
        data: {
          id: reportId,
          message: 'Report saved offline. It will be submitted when you reconnect (without image).'
        }
      };
    }
    
    // Try storing the report with image
    let reportId;
    try {
      reportId = await storeOfflineReport(reportData, imageFile);
      console.log('Offline report stored with ID (with image):', reportId);
    } catch (imageError) {
      console.error('Error storing image with report:', imageError);
      // If image storage fails, try storing just the report data
      reportId = await storeOfflineReport(reportData, null);
      console.log('Offline report stored with ID (without image due to error):', reportId);
    }
    
    // Try to register background sync
    const syncRegistered = await registerBackgroundSync();
    console.log('Background sync registered:', syncRegistered);
    
    // Return a success response similar to what the API would return
    return {
      success: true,
      offline: true,
      data: {
        id: reportId,
        message: 'Report saved offline. It will be submitted when you reconnect.'
      }
    };
  } catch (error) {
    console.error('Error handling offline submission:', error);
    throw error;
  }
};

/**
 * Get recent tsunami reports
 * @returns {Promise<Array>} Array of tsunami reports
 */
export const getRecentReports = async () => {
  try {
    const data = await fetchAuthenticated('/reports/recent');
    // Extract reports and filter out those with no coordinates
    const reports = data.data?.reports || [];
    return reports.filter(report => 
      report.latitude !== null && report.longitude !== null
    );
  } catch (error) {
    console.error('Error fetching recent reports:', error);
    return [];
  }
};

/**
 * Submit a new tsunami report
 * @param {FormData} formData - The report data as FormData
 * @returns {Promise<Object>} The API response
 */
export const submitReport = async (formData) => {
  // Create a new FormData object to ensure proper structure
  const cleanFormData = new FormData();
  
  // First extract and log all entries for debugging
  console.log('Original FormData contents:');
  let reportData = null;
  let imageFile = null;
  
  for (const [key, value] of formData.entries()) {
    if (key === 'images') {
      imageFile = value;
      console.log(`Found image in FormData: ${value.name} (${value.type}, ${value.size} bytes)`);
    } else if (key === 'data') {
      try {
        reportData = JSON.parse(value);
        console.log(`Found report data in FormData: ${JSON.stringify(reportData).substring(0, 100)}...`);
      } catch (e) {
        console.log(`Found data but couldn't parse: ${value}`);
      }
    }
  }
  
  // Now build a clean FormData object with the correct structure
  if (reportData) {
    cleanFormData.append('data', JSON.stringify(reportData));
  }
  
  if (imageFile) {
    // Ensure the image is added with the correct field name
    cleanFormData.append('images', imageFile);
    console.log(`Added image to new FormData: ${imageFile.name}`);
  }
  
  // Log the final FormData to be sent
  console.log('Final FormData contents:');
  for (const [key, value] of cleanFormData.entries()) {
    if (key === 'images') {
      console.log(`- ${key}: File object ${value.name} (${value.type}, ${value.size} bytes)`);
    } else if (key === 'data') {
      try {
        const parsedData = JSON.parse(value);
        console.log(`- ${key}: ${JSON.stringify(parsedData).substring(0, 100)}...`);
      } catch (e) {
        console.log(`- ${key}: ${value}`);
      }
    }
  }
  
  // Use a special fetch call to display more debug information
  try {
    const token = await getAuthToken();
    
    // Make the fetch request with the clean FormData
    console.log('Sending POST request to API with cleaned FormData');
    const response = await fetch(`${API_BASE_URL}/reports`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
        // Don't set Content-Type for multipart/form-data
      },
      body: cleanFormData
    });
    
    if (!response.ok) {
      throw new Error(`Server returned ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    
    // Check for API errors
    if (!result.success) {
      throw new Error(result.error || 'API error');
    }
    
    console.log('Server response:', result);
    return result;
  } catch (error) {
    // If offline or network error, handle offline submission
    if (!navigator.onLine || 
        error.name === 'TypeError' || 
        error.message.includes('Failed to fetch')) {
      console.log('Network error or offline - storing for later submission');
      return handleOfflineSubmission(cleanFormData);
    }
    
    console.error('API request failed:', error);
    throw error;
  }
};

/**
 * Convert a File object to a base64 string
 * @param {File} file - The file to convert
 * @returns {Promise<string>} A base64 string representation of the file
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Sync offline reports with the server
 * This will get all pending reports and try to submit them
 * @returns {Promise<Object[]>} Array of sync results
 */
export const syncOfflineReports = async () => {
  if (!navigator.onLine) {
    console.log('Still offline, cannot sync reports');
    return { success: 0, failed: 0 };
  }

  console.log('Starting offline reports sync');
  const db = await initDatabase();
  const tx = db.transaction('offline-reports', 'readonly');
  const store = tx.objectStore('offline-reports');
  const reports = await store.getAll();
  
  if (reports.length === 0) {
    console.log('No offline reports to sync');
    return { success: 0, failed: 0 };
  }
  
  console.log(`Found ${reports.length} offline reports to sync`);
  
  let successCount = 0;
  let failedCount = 0;
  
  for (const report of reports) {
    if (report.synced) continue;
    
    try {
      console.log(`Attempting to sync report ${report.id}`);
      
      // Create FormData for the submission
      const formData = new FormData();
      formData.append('reportData', JSON.stringify(report.data));
      
      // Convert base64 image back to blob if it exists
      if (report.imageBase64) {
        try {
          const blob = await base64ToBlob(report.imageBase64);
          formData.append('image', blob, 'image.jpg');
        } catch (error) {
          console.error('Failed to convert base64 to blob:', error);
          // Continue without the image
        }
      }
      
      // Submit the report
      const response = await fetch(`${API_BASE_URL}/api/reports`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }
      
      // Mark as synced
      const updateTx = db.transaction('offline-reports', 'readwrite');
      const updateStore = updateTx.objectStore('offline-reports');
      report.synced = true;
      report.lastAttempt = Date.now();
      await updateStore.put(report);
      await updateTx.complete;
      
      console.log(`Successfully synced report ${report.id}`);
      successCount++;
    } catch (error) {
      console.error(`Failed to sync report ${report.id}:`, error);
      
      // Update attempt count
      const updateTx = db.transaction('offline-reports', 'readwrite');
      const updateStore = updateTx.objectStore('offline-reports');
      report.attempts += 1;
      report.lastAttempt = Date.now();
      await updateStore.put(report);
      await updateTx.complete;
      
      failedCount++;
    }
  }
  
  console.log(`Sync complete: ${successCount} successful, ${failedCount} failed`);
  return { success: successCount, failed: failedCount };
};

/**
 * Convert a base64 string to a Blob
 * @param {string} base64String - The base64 string to convert
 * @returns {Promise<Blob>} A Blob created from the base64 string
 */
const base64ToBlob = async (base64String) => {
  // Extract the base64 data without the data URL prefix
  const parts = base64String.split(';base64,');
  const contentType = parts[0].split(':')[1] || 'image/jpeg';
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  
  // Convert to ArrayBuffer
  const array = new Uint8Array(new ArrayBuffer(rawLength));
  for (let i = 0; i < rawLength; i++) {
    array[i] = raw.charCodeAt(i);
  }
  
  return new Blob([array], { type: contentType });
};

/**
 * Get all tsunami reports
 * @param {Object} options - Optional parameters for filtering
 * @param {number} options.limit - Maximum number of reports to retrieve (default: 100)
 * @param {number} options.offset - Number of reports to skip (default: 0)
 * @returns {Promise<Object>} Object containing report data and count
 */
export const getAllReports = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    
    // Add optional parameters if provided
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    
    // Build URL with parameters
    const url = `/reports/recent${params.toString() ? '?' + params.toString() : ''}`;
    
    const data = await fetchAuthenticated(url);
    
    return {
      reports: data.data?.reports || [],
      count: data.data?.count || 0,
      timeframe: data.data?.timeframe || ''
    };
  } catch (error) {
    console.error('Error fetching all reports:', error);
    return { reports: [], count: 0, timeframe: '' };
  }
}; 